import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export interface CurrentUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  isAnonymous: boolean;
  audioOffsetMs: number;
  videoOffsetMs: number;
}

export type AuthStatus = 'initialising' | 'ready' | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
  setUser: (u: CurrentUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TEST_MODE_KEY = 'gd-test-uid';

function getTestModeUid(): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get('testUid');
  if (fromQuery) {
    sessionStorage.setItem(TEST_MODE_KEY, fromQuery);
    return fromQuery;
  }
  return sessionStorage.getItem(TEST_MODE_KEY);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('initialising');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const testUid = getTestModeUid();
        if (testUid) {
          const me = await fetchMe();
          if (cancelled) return;
          setUser({
            uid: testUid,
            email: me.email,
            displayName: me.displayName,
            isAnonymous: me.isAnonymous,
            audioOffsetMs: me.audioOffsetMs,
            videoOffsetMs: me.videoOffsetMs,
          });
          setStatus('ready');
          return;
        }

        const { initFirebaseAuth, ensureSignedIn, isFirebaseConfigured } = await import('./firebase');
        if (!isFirebaseConfigured()) {
          throw new Error(
            'Firebase is not configured. Set VITE_FIREBASE_* in your .env, or append ?testUid=devfallback-... for offline testing.',
          );
        }
        await initFirebaseAuth();
        const fbUser = await ensureSignedIn();
        if (cancelled) return;
        const me = await fetchMe();
        if (cancelled) return;
        setUser({
          uid: fbUser.uid,
          email: me.email,
          displayName: me.displayName,
          isAnonymous: me.isAnonymous,
          audioOffsetMs: me.audioOffsetMs,
          videoOffsetMs: me.videoOffsetMs,
        });
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error('AuthProvider init failed:', msg);
        setError(msg);
        setStatus('error');
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshMe = async () => {
    const me = await fetchMe();
    setUser((prev) =>
      prev
        ? {
            ...prev,
            email: me.email,
            displayName: me.displayName,
            isAnonymous: me.isAnonymous,
            audioOffsetMs: me.audioOffsetMs,
            videoOffsetMs: me.videoOffsetMs,
          }
        : null,
    );
  };

  const signInWithGoogleHandler = async () => {
    if (getTestModeUid()) {
      throw new Error('Test mode does not support Google sign-in. Switch off ?testUid=.');
    }
    const { signInWithGoogle } = await import('./firebase');
    await signInWithGoogle();
    // Re-attribute the anonymous events emitted earlier in this browser
    // session to the freshly authenticated user. Without this, plays
    // before sign-in stay tagged `anon` in the dashboard even though
    // the player is signed in by the time they look at it.
    //
    // Drain the analytics buffer before the claim so anything queued
    // gets flushed under the *new* credentials (server tags by the
    // request's auth uid at insert time). Already-in-flight POSTs
    // sent under the prior anon token can still race the claim and
    // arrive after the UPDATE has run — those rows leak through as
    // anon, but the window is sub-second human-paced and acceptable
    // (see analytics-overview.mdc). Best-effort throughout: a failure
    // here must not block sign-in.
    try {
      const { getAnalyticsClient } = await import('../analytics/analyticsClient');
      await getAnalyticsClient().flushAll('manual');
    } catch (err) {
      console.warn('Pre-claim analytics flush failed (ignored):', err);
    }
    try {
      const { authFetch } = await import('../api');
      const res = await authFetch('/api/sessions/claim', { method: 'POST' });
      if (!res.ok) {
        console.warn(`Session claim failed: ${res.status}`);
      }
    } catch (err) {
      console.warn('Session claim request errored (ignored):', err);
    }
    await refreshMe();
  };

  const signOutHandler = async () => {
    if (getTestModeUid()) {
      sessionStorage.removeItem(TEST_MODE_KEY);
      setUser(null);
      setStatus('initialising');
      window.location.reload();
      return;
    }
    const { signOutAll } = await import('./firebase');
    await signOutAll();
    setUser(null);
    setStatus('initialising');
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        error,
        signInWithGoogle: signInWithGoogleHandler,
        signOut: signOutHandler,
        refreshMe,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

async function fetchMe(): Promise<CurrentUser> {
  const { authFetch } = await import('../api');
  const res = await authFetch('/api/me');
  if (!res.ok) {
    throw new Error(`/api/me failed: ${res.status}`);
  }
  const json = (await res.json()) as CurrentUser;
  return json;
}
